<template>
    <div class="nav-bar">
        <b-row v-if="!!maskUser.id" class="masking">
            {{ $i18n.gt('MASKING_AS') }}
            <template v-if="maskUser.firstName && maskUser.lastName">
                {{ `${maskUser.firstName} ${maskUser.lastName}` }}
            </template>
            <template v-else>
                {{ maskUser.email }}
            </template>
            <b-btn variant="link" class="ml-3 p-0" @click="unmask"
                   v-b-popover.bottom.hover="$i18n.gt('STOP_MASKING')"
                   :aria-label="$i18n.gt('STOP_MASKING')">
                <b-icon-person-x-fill aria-hidden="true"/>
            </b-btn>
        </b-row>
        <b-navbar>
            <b-navbar-brand @click="navigate('/')">
                <img src="../../assets/images/logo.png" alt="Greenhouse Scout">
            </b-navbar-brand>

            <!-- toggle mobile nav (only shows on smaller screens) -->
            <b-btn variant="link"
                   class="mobile-nav-toggle p-0"
                   v-if="$mq !== 'xl'"
                   @click="toggleMobileNav()"
                   @keydown.enter.prevent="toggleMobileNav()"
                   @keydown.space.prevent="toggleMobileNav()"
                   :aria-label="$i18n.gt(showMobileNav ? 'CLOSE_MENU_LABEL' : 'OPEN_MENU_LABEL')"
                   :aria-expanded="String(showMobileNav)">
                <span class="hamburger" :class="{'is-open': showMobileNav}" aria-hidden="true">
                    <span class="hamburger-bar hamburger-bar--top"></span>
                    <span class="hamburger-bar hamburger-bar--middle"></span>
                    <span class="hamburger-bar hamburger-bar--bottom"></span>
                </span>
            </b-btn>

            <!-- language selection -->
            <b-row class="lang"
                   role="button"
                   tabindex="0"
                   @click="toggleLang"
                   @keydown.enter.prevent="toggleLang"
                   @keydown.space.prevent="toggleLang"
                   :aria-label="'Switch language to ' + otherLang"
                   v-if="$mq === 'xl'">
                <img v-if="currentLang === 'en'" class="flag" src="../../assets/icons/spanish.svg" alt="">
                <img v-else class="flag" src="../../assets/icons/english.svg" alt="">
                <span class="lang-text">{{otherLang}}</span>
            </b-row>

            <b-navbar-nav class="ml-auto nav-items" v-if="$mq === 'xl'">
                <!-- public actions -->
                <template v-if="!loggedIn">
                    <!-- signup is only available on mobile, but the route can still be accessed in dev
                    <b-nav-item @click="navigate('/signup')">{{$i18n.gt('SIGNUP')}}</b-nav-item>-->
                    <b-nav-item @click="navigate('/tutorial')" class="no-border">{{$i18n.gt('TUTORIAL')}}</b-nav-item>
                    <b-nav-item @click="navigate('/about')" class="no-border">{{$i18n.gt('ABOUT')}}</b-nav-item>
                    <b-nav-item @click="navigate('/contact')" class="no-border">{{$i18n.gt('CONTACT')}}</b-nav-item>
                    <b-nav-item @click="navigate('/login')" class="no-border">{{$i18n.gt('LOGIN')}}</b-nav-item>
                </template>

                <!-- logged in actions -->
                <template v-else>
                    <!-- admin dropdown -->
                    <b-nav-item-dropdown v-if="isAdmin" :text="$i18n.gt('ADMIN')" right class="pr-3 no-border">
                        <b-dropdown-item @click="navigate('/users')">{{$i18n.gt('USER_MANAGEMENT')}}</b-dropdown-item>
                        <b-dropdown-item @click="navigate('/database')">{{$i18n.gt('DATABASE_MANAGEMENT')}}</b-dropdown-item>
                    </b-nav-item-dropdown>

<!--                    <b-nav-item @click="sync"><b-icon-arrow-repeat/></b-nav-item>-->
                    <b-nav-item @click="navigate('/contact')" :aria-label="$i18n.gt('CONTACT_LABEL')"><b-icon-envelope-fill/></b-nav-item>
                    <b-nav-item @click="navigate('/tutorial')" :aria-label="$i18n.gt('HELP_LABEL')"><b-icon-question-circle-fill/></b-nav-item>

                    <!-- user dropdown -->
                    <b-nav-item-dropdown :text="$i18n.gt('PROFILE_LABEL')" class="no-chevron" right>
                        <template #button-content>
                            <b-icon-person-fill/>
                        </template>
                        <b-dropdown-item @click="navigate('/profile')" :disabled="!!maskUser.email">{{username}}</b-dropdown-item>
                        <b-dropdown-item @click="logoutUser" :disabled="!!maskUser.email">{{$i18n.gt('LOGOUT')}}</b-dropdown-item>
                        <b-dropdown-item @click="unmask" v-if="!!maskUser.email">{{$i18n.gt('STOP_MASKING')}}</b-dropdown-item>
                    </b-nav-item-dropdown>
                </template>
            </b-navbar-nav>
        </b-navbar>

        <!-- mobile nav sidebar -->
        <b-sidebar
            v-if="$mq !=='xl'"
            v-model="showMobileNav"
            class="mobile-sidebar"
            no-header
            shadow
            backdrop
            >
            <div class="sidebar-content">
                <template v-if="loggedIn">
                    <b-btn variant="outline" @click="navigate('/locations')">
                        <span class="icon-mask icon-mask--green" :style="iconMask('house.svg')"></span>
                        {{ $i18n.gt('LOCATIONS_LABEL') }}
                    </b-btn>
                    <b-btn variant="outline" @click="navigate('/tasks')">
                        <span class="icon-mask icon-mask--green" :style="iconMask('card-checklist.svg')" aria-hidden="true"></span>
                        {{ $i18n.gt('TASKS_LABEL') }}
                    </b-btn>

                    <b-btn variant="success" @click="navigate('/scouts/create')">
                        <span class="icon-mask icon-mask--white" :style="iconMask('clipboard-check.svg')"></span>
                        {{ $i18n.gt('SCOUT_LABEL') }}
                    </b-btn>
                    <b-btn variant="success" @click="navigate('/applications/create')">
                        <span class="icon-mask icon-mask--white" :style="iconMask('box-arrow-down-right.svg')"></span>
                        {{ $i18n.gt('APPLICATION_LABEL') }}
                    </b-btn>

                    <b-btn variant="ghost" @click="navigate('/pests')">
                        <span class="icon-mask icon-mask--tan" :style="iconMask('pest.svg')"></span>
                        {{ $i18n.gt('PEST_INSECTS_LABEL') }}
                    </b-btn>
                    <b-btn variant="ghost" @click="navigate('/beneficials')">
                        <span class="icon-mask icon-mask--tan" :style="iconMask('wasp.svg')"></span>
                        {{ $i18n.gt('BENEFICIALS_LABEL') }}
                    </b-btn>
                    <b-btn variant="ghost" @click="navigate('/symptoms')">
                        <span class="icon-mask icon-mask--tan" :style="iconMask('clipboard-pulse.svg')"></span>
                        {{ $i18n.gt('SYMPTOMS_LABEL') }}
                    </b-btn>
                    <b-btn variant="ghost" @click="navigate('/diseases')">
                        <span class="icon-mask icon-mask--tan" :style="iconMask('leaf-eaten.svg')"></span>
                        {{ $i18n.gt('DISEASES_LABEL') }}
                    </b-btn>
                    <b-btn variant="ghost" @click="navigate('/interactions')" class="small-es">
                        <span class="icon-mask icon-mask--tan" :style="iconMask('spray.svg')"></span>
                        {{ $i18n.gt('PESTICIDE_INTERACTIONS_LABEL') }}
                    </b-btn>
                    <b-btn variant="ghost" @click="navigate('/pesticides')">
                        <span class="icon-mask icon-mask--tan" :style="iconMask('spray.svg')"></span>
                        {{ $i18n.gt('PESTICIDES_LABEL') }}
                    </b-btn>
                    <hr>
                </template>
                <div class="user-buttons">
                    <b-btn @click="toggleLang" :aria-label="'Switch language to ' + otherLang">
                        <img v-if="currentLang === 'en'" src="../../assets/icons/spanish.svg" alt="">
                        <img v-else class="flag" src="../../assets/icons/english.svg" alt="">
                        <span class="lang-text">{{otherLang}}</span>
                    </b-btn>
                    <template v-if="!loggedIn">
                        <!-- signup is only available on mobile, but the route can still be accessed in dev
                        <b-btn @click="navigate('/signup')">{{$i18n.gt('SIGNUP')}}</b-btn>-->
                        <b-btn @click="navigate('/tutorial')">{{$i18n.gt('TUTORIAL')}}</b-btn>
                        <b-btn @click="navigate('/about')">{{$i18n.gt('ABOUT')}}</b-btn>
                        <b-btn @click="navigate('/contact')">{{$i18n.gt('CONTACT')}}</b-btn>
                        <b-btn @click="navigate('/login')">{{$i18n.gt('LOGIN')}}</b-btn>
                    </template>
                    <template v-else>
<!--                        <b-btn @click="sync"><b-icon-arrow-repeat class="mr-2"/>{{ $i18n.gt('SYNC_LABEL') }}</b-btn>-->
                        <b-btn @click="navigate('/contact')"><b-icon-envelope/>{{ $i18n.gt('CONTACT_LABEL') }}</b-btn>
                        <b-btn @click="navigate('/tutorial')"><b-icon-question-circle-fill/>{{ $i18n.gt('HELP_LABEL') }}</b-btn>
                        <b-btn @click="navigate('/profile')"><b-icon-person-circle/>{{ $i18n.gt('PROFILE_LABEL') }}</b-btn>
                        <b-btn @click="logoutUser">{{ $i18n.gt('LOGOUT') }}</b-btn>
                    </template>
                </div>
            </div>
        </b-sidebar>
    </div>
</template>

<script>
import { BSidebar } from 'bootstrap-vue'
export default {
    name: 'NavBar',
    components: { BSidebar },
    data() {
        return {
            showMobileNav: false
        }
    },
    computed: {
        maskUser() {
            return this.$store.state.maskUser
        },
        loggedIn () {
            return !!this.$store.state.currentUser.uid;
        },
        username () {
            const user = this.maskUser.email ? this.maskUser : this.$store.state.currentDbUser
            const email = user.email ?? ''
            let name = null
            if (user.firstName && user.lastName) {
                user.name = user.firstName + ' ' + user.lastName
            }
            return user.name ? user.name : email.substring(0, email.lastIndexOf("@"));
        },
        otherLang () {
            return this.currentLang === 'en' ? 'Español' : 'English'
        },
        currentLang() {
            return this.$store.state.currentLang
        },
        isAdmin() {
            if (this.maskUser.email) {
                return this.maskUser.admin
            } else return !!this.$store.state.currentDbUser.admin
        }
    },
    methods: {
        navigate(route) {
            this.$router.push(route)
        },
        iconMask(path) {
            const url = require(`../../assets/icons/${path}`)
            return {
                maskImage: `url(${url})`,
                webkitMaskImage: `url(${url})`,
            }
        },
        logoutUser () {
            this.$api.logoutUser(this)
        },
        toggleLang () {
            this.$i18n.switchLanguage()
        },
        sync () {
            console.log('sync')
        },
        toggleMobileNav() {
            this.showMobileNav = !this.showMobileNav
        },
        unmask() {
            this.$store.state.maskUser = {}
            sessionStorage.removeItem('maskUser')
            this.$router.push('/')
        },
    }
}

</script>

<style lang="scss">

@import '/sass/variables_mixins';

div.nav-bar {
    border-bottom-style: solid;
    border-bottom-width: 4px;
    border-bottom-color: $neutral-light;

    .navbar-brand {
        cursor: pointer;
    }
    .mobile-nav-toggle {
        .hamburger {
            position: relative;
            width: 36px;
            height: 24px;
            display: block;

            .hamburger-bar {
                position: absolute;
                left: 0;
                width: 100%;
                height: 3px;
                background-color: #3C964E;
                border-radius: 2px;
                transition: all 0.25s ease-in-out;

                &--top { top: 0; }
                &--middle { top: 11px; }
                &--bottom { top: 22px; }
            }

            &.is-open {
                .hamburger-bar--top {
                    top: 9px;
                    transform: rotate(45deg);
                }
                .hamburger-bar--middle {
                    opacity: 0;
                }
                .hamburger-bar--bottom {
                    top: 9px;
                    transform: rotate(-45deg);
                }
            }
        }
    }
    .no-chevron {
        .dropdown-toggle::after {
            content: none;
        }
    }
    .lang {
        display: flex;
        justify-content: center;
        align-content: center;
        gap:.5rem;
        cursor: pointer;
        box-sizing: border-box;
        position: absolute;
        left:50%;
        transform:translateX(-50%);
        transition:all .3s;
        top: 0px;
        width: 134px;
        height: 42px;
        background-color: #DFECDA;
        //background-color: lighten($light-green,5%);

        border-width: 0px 2px 2px 2px;
        border-style: solid;
        border-color: #AACBB0;
        border-radius: 0px 0px 5px 5px;

        .lang-text {
            font-weight: 700;
            font-size: 14px;
            text-align: center;
            color: darken($green,10%);
        }
        .flag {
            width: 24px;
            height: 24px;
        }
        &:hover {
            width:154px;
            background-color: $light-green--hover;
        }
    }

    .b-icon {
        width:26px;
        height:26px;
    }

    .dropdown {
        li:not(:last-child) {
            border-bottom: 1px solid #e9ecef;
        }
    }

    .dropdown-item {
        padding:.5rem 1rem;
    }

    .nav-items {
        >.nav-item {

            &:not(.no-border) {
                > a.nav-link {
                    color: #6a8d71!important;
                    background-color: $neutral-light-green;
                    border: solid 2px #b7d8bd;
                    border-radius: 50%;
                    transition: all .2s;
                    &:hover, &:focus, &:active {
                        border-color:$green;
                        color:#fff!important;
                        background-color: $green;
                    }
                }
            }
            > a.nav-link {
                font-weight: 700;
                font-size: 14px;
                text-align: center;
                margin-left: .5rem;
            }
        }
    }
}

.navbar-light .navbar-nav {

    .nav-link {
        //color: #465046!important;
        color: #666;
        &:hover {
            color:$green;
        }
    }

    .dropdown-item:hover, .dropdown-item:focus {
        background-color: #3C964E;
        color:#fff;
    }

}

.navbar-expand {
    justify-content: space-between;
}

@media (max-width:1023px) {
    .navbar-brand {
        max-width:240px;
        img {
            max-width:100%;
        }
    }
    .b-sidebar-outer {
       // position: fixed;
        //top: 0;
        //left: 0;
        //width: 80vw;
        //z-index: 10;
       // height: 100vh;

        .sidebar-content {
            overflow-y: scroll;
            -webkit-overflow-scrolling:touch; // mobile safari
            height: 100vh;
            background-color: #D6EDCE;
            padding: calc(env(safe-area-inset-top, 0) + 1.5em)
                calc(env(safe-area-inset-right, 0) + 1.5em)
                calc(env(safe-area-inset-bottom, 0) + 1.5em)
                calc(env(safe-area-inset-left, 0) + 1.5em);

            button {
                width: 100%;
                margin: .5em 0;
                display: inline-flex;
                align-items: center;
                gap: .5rem;
                padding: 6px 24px;
            }
            hr {
                width: 84px;
                height: 3px;
                border-radius: 4px;
                background: #076A1A;
            }
            .user-buttons {
                display: flex;
                flex-wrap:wrap;
                justify-content: center;
                .b-dropdown {
                    width: 100%;
                }
                .b-icon {
                    font-size: 24px;
                }
                button {
                    width: 66%;
                    //background: #E8F2E4;
                    background: lighten($light-green,8%);
                    background: rgba(255,255,255,.3);
                    border-radius: 3px;
                    //border: 2px solid #076A1A;
                    border: 1px solid #9dc0a3;
                    color: #076A1A;
                    justify-content: center;
                }
            }
        }
    }
}

@media (min-width:1024px) {
    .navbar-brand {
        max-width:280px;
        transition: opacity .3s;
        &:hover {
            opacity:.5;
        }
    }

}

.masking {
    width: 100%;
    margin: 0 0;
    background-color: #004B23;
    color: white;
    justify-content: center;
    height: 2.5em;
    align-content: center;

    svg.bi-person-x-fill {
        cursor: pointer;
    }
}
header.header {
    height: auto;
}
.icon-mask {
    display: inline-block;
    width: 24px;
    height: 24px;
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    flex-shrink: 0;

    &--green { background-color: #2d863e; }
    &--white { background-color: #fff; }
    &--tan   { background-color: #8b551c; }
}
</style>
